import { memo, useCallback, useMemo } from "react";

import Event from "@mui/icons-material/Event";
import type { DateTimePickerProps } from "@mui/x-date-pickers-pro";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import isNil from "lodash/isNil";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { MOBILE_MEDIA_QUERY } from "@waterly/utils-isomorphic";

import { useDateFormats } from "../../../providers/DateFormatsProvider";
import { DatePickerActionBar } from "../DatePickerActionBar";

const localTimeZone = DateTime.now().zoneName;

export type BasicDateTimePickerProps = Omit<
  DateTimePickerProps<DateTime>,
  "value" | "onChange" | "onAccept" | "format" | "disableHighlightToday"
> & {
  //value should be in the timezone that you want to be displayed. (i.e. the value should already be in the proper timeZone)
  value: DateTime | null;
  onChange: Required<DateTimePickerProps<DateTime>>["onChange"];
  onAccept: Required<DateTimePickerProps<DateTime>>["onAccept"];
  //timeZone onChange and onAccept the time will be returned in
  timeZone?: string;
  hideZoneHelperText?: boolean;
};

export type BasicDateTimeWidgetProps = BasicDateTimePickerProps;

const BasicDateTimePickerComponent = ({
  value,
  onChange,
  onAccept,
  slotProps,
  slots,
  timeZone = localTimeZone,
  hideZoneHelperText = false,
  ...props
}: BasicDateTimePickerProps) => {
  const { t } = useTranslation(["common"]);
  const { dateTime: dateTimeFormat } = useDateFormats();

  const applyTimeZone = useCallback(
    (dateTime: DateTime | null, timeZone: string): DateTime | null =>
      dateTime ? DateTime.fromObject(dateTime.toObject(), { zone: timeZone }) : null,
    [],
  );

  /**
   * This is the Localized DateTime for display purposes only
   */
  const displayDateTime = useMemo<DateTime | null>(() => {
    if (!value) return null;
    return applyTimeZone(value, localTimeZone);
  }, [applyTimeZone, value]);

  const handleOnChange = useCallback<Required<DateTimePickerProps<DateTime>>["onChange"]>(
    (dateTime, keyboardInputValue) => {
      if (!dateTime) return onChange(null, keyboardInputValue);
      onChange(applyTimeZone(dateTime, timeZone), keyboardInputValue);
    },
    [applyTimeZone, onChange, timeZone],
  );

  const handleOnAccept = useCallback<Required<DateTimePickerProps<DateTime>>["onAccept"]>(
    (dateTime, context) => {
      if (!dateTime) return onAccept(null, context);
      onAccept(applyTimeZone(dateTime, timeZone), context);
    },
    [applyTimeZone, onAccept, timeZone],
  );

  /**
   * This is a live check that reflects what the timezone being displayed is, DO NOT REMOVE!!!
   * aka it reflects the value prop, not the timeZone prop
   */
  const zoneLabel = useMemo(() => {
    if (hideZoneHelperText) return "";
    const zone = value?.zoneName;
    return isNil(zone) ? "" : t("common:datepicker_timezone_label", { zoneName: zone });
  }, [value?.zoneName, t, hideZoneHelperText]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-us">
      <DateTimePicker
        value={displayDateTime}
        onChange={handleOnChange}
        onAccept={handleOnAccept}
        disableOpenPicker={false}
        closeOnSelect // This must be set to true, otherwise the time part of the picker breaks the whole component
        ampmInClock
        slotProps={{
          toolbar: { hidden: true },
          actionBar: { actions: ["cancel", "today"] },
          textField: {
            fullWidth: true,
            InputProps: { endAdornment: <Event /> },
            helperText: zoneLabel,
          },
          ...slotProps,
        }}
        slots={{
          actionBar: DatePickerActionBar,
          ...slots,
        }}
        {...props}
        desktopModeMediaQuery={MOBILE_MEDIA_QUERY}
        format={dateTimeFormat}
        // Highlighting today doesn't honor timezone. To avoid confusion, we always disable it for now.
        disableHighlightToday
      />
    </LocalizationProvider>
  );
};

export const BasicDateTimePicker = memo(BasicDateTimePickerComponent);

export default BasicDateTimePicker;
